import { makeObservable, observable, computed, action } from 'mobx'
/**
 * LstTypeBase is a generic base class for managing a list of items of a given class.
 *
 * @template T
 */
export class LstTypeBase {
	list = []
	ItemClass = null

	constructor(ItemClass, list = []) {
		// ItemClass is a class that will be used to create new items in the list
		this.ItemClass = ItemClass

		// list is an array of objects that will be used to create new items in the list
		this.list = list.map((itm) => new ItemClass(itm))

		makeObservable(this, {
			list: observable,
			updateItem: action.bound,
			addItem: action.bound,
			deleteItem: action.bound,
			getItemById: action.bound,
			itemsCount: computed
		})
	}

	/**
	 * Prepares the list for saving by returning a raw data array,
	 * ensuring nested comments arrays are serialized.
	 *
	 * @returns {Object[]} Array of items ready for persistence.
	 */
	prepareForSave() {
		if (!Array.isArray(this.list)) {
			return [] // Ensure the function returns an empty array if this.list is not an array
		}

		return this.list.map((item) => {
			if (!item.comments) {
				return item
			}
			// Ensure item.comments.list is an array before mapping
			const comments = Array.isArray(item.comments.list)
				? item.comments.list.map((comment) => comment)
				: [] // Return an empty array if item.comments.list is not an array

			return {
				...item,
				comments
			}
		})
	}

	/**
	 * Updates an existing item in the list, or adds it if not found.
	 *
	 * @param {T} item - The item instance to update or add.
	 */
	updateItem(item) {
		const index = this.list.findIndex((s) => s.id === item.id)
		if (index >= 0) {
			this.list[index] = item
		} else {
			this.addItem(item)
		}
	}

	/**
	 * Adds a new item to the list.
	 *
	 * @param {Object} [data={}] - Raw data for the new item.
	 */
	addItem(data = {}) {
		const item = new this.ItemClass(data)
		this.list.push(item)
	}

	/**
	 * Deletes an item from the list by its ID.
	 *
	 * @param {string} itemId - The ID of the item to remove.
	 */
	deleteItem(itemId) {
		this.list = this.list.filter((item) => item.id !== itemId)
	}

	/**
	 * Retrieves an item from the list by its ID.
	 *
	 * @param {string} itemId - The ID of the item to find.
	 * @returns {T|undefined} The found item or undefined.
	 */
	getItemById(itemId) {
		return this.list.find((item) => item.id === itemId)
	}

	/**
	 * Returns the number of items in the list.
	 *
	 * @type {number}
	 * @computed
	 */
	get itemsCount() {
		return this.list.length
	}
}
